import { useEffect, useState } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { qaAttr, type QaAttrOptions } from '@/presentation/utils/qaAttr';

interface ExpauseLoadingIndicatorProps {
  size?: number;
  color?: string;
  animationDuration?: number;
  addInsideCircleParent?: boolean;
  circleParentColor?: string;
  circleParentSize?: number;
  startDelayMillis?: number;
  /**
   * Optional QA tag override; defaults to `{ id: 'loading-indicator', status: 'loading' }`.
   *
   * The default id is intentionally shared across every spinner so each one is
   * findable with zero per-call-site work, and the common qa-tester pattern is
   * to wait for the *absence* of any `loading-indicator` rather than to assert
   * on a specific one. Because the default is shared, any page that can render
   * two spinners concurrently (e.g. an inline send-button spinner plus a
   * full-page first-load indicator, or a list-footer "loading more" spinner plus
   * a content loader) must pass a disambiguating override on at least one of
   * them — e.g. `qa={{ id: 'feature-spinner', status: 'loading' }}` — so the
   * `qa-tester` can resolve each spinner to a single DOM node.
   */
  qa?: QaAttrOptions;
}

/**
 * ExpauseLoadingIndicator
 * 
 * React equivalent of Flutter's ExpauseLoadingIndicator.
 * Animates two rectangles from parallel (||) to X-shaped position and back.
 */
export const ExpauseLoadingIndicator = ({
  size = 16,
  color,
  animationDuration = 450,
  addInsideCircleParent = false,
  circleParentColor,
  circleParentSize = 60,
  startDelayMillis = 50,
  qa,
}: ExpauseLoadingIndicatorProps) => {
  const { theme } = useTheme();
  const [isAnimating, setIsAnimating] = useState(false);
  const qaProps = qaAttr(qa ?? { id: 'loading-indicator', status: 'loading' });

  // Use theme colors as defaults if not provided
  const indicatorColor = color ?? theme.exclusive;
  const parentColor = circleParentColor ?? theme.secondary;

  // Calculate dimensions matching Flutter logic
  const rectangleWidth = size / 4;
  const rectangleHeight = size;
  const gap = size / 5;
  const containerWidth = (rectangleWidth * 2) + gap;
  // Calculate translation so rectangles overlap at center when rotated
  const translationDistance = (gap + rectangleWidth) / 2;

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsAnimating(true);
    }, startDelayMillis);

    return () => clearTimeout(timer);
  }, [startDelayMillis]);

  const rectangleStyle: React.CSSProperties = {
    width: `${rectangleWidth}px`,
    height: `${rectangleHeight}px`,
    backgroundColor: indicatorColor,
    borderRadius: '4px',
    position: 'absolute',
  };

  const loadingIndicator = (
    <div
      {...(addInsideCircleParent ? {} : qaProps)}
      style={{
        width: `${containerWidth}px`,
        height: `${rectangleHeight}px`,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* Left rectangle - starts at left position, moves right and rotates */}
      <div
        style={{
          ...rectangleStyle,
          left: '0',
          animation: isAnimating ? `expause-loading-left ${animationDuration}ms ease-in-out infinite alternate` : 'none',
        }}
      />

      {/* Right rectangle - starts at right position, moves left and rotates */}
      <div
        style={{
          ...rectangleStyle,
          right: '0',
          animation: isAnimating ? `expause-loading-right ${animationDuration}ms ease-in-out infinite alternate` : 'none',
        }}
      />

      <style>
        {`
          @keyframes expause-loading-left {
            from {
              transform: translate(0, 0) rotate(0deg);
            }
            to {
              transform: translate(${translationDistance}px, 0) rotate(-45deg);
            }
          }

          @keyframes expause-loading-right {
            from {
              transform: translate(0, 0) rotate(0deg);
            }
            to {
              transform: translate(-${translationDistance}px, 0) rotate(45deg);
            }
          }
        `}
      </style>
    </div>
  );

  if (addInsideCircleParent) {
    return (
      <div
        {...qaProps}
        style={{
          width: `${circleParentSize}px`,
          height: `${circleParentSize}px`,
          borderRadius: '50%',
          backgroundColor: parentColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {loadingIndicator}
      </div>
    );
  }

  return loadingIndicator;
};
